import { QueryParameterBag } from '@smithy/types'

import {
  DroidMapMetadata,
  OffsetPaged,
  ServiceClient,
  ServiceClientContext,
  queryParameterBagFromOffsetPaginationControls,
} from '@droidmap/shared-service-contract'

import {
  CreateFolderInput,
  DeleteFolderInput,
  Folder,
  ListFolderTreeInput,
  ListFolderTreeResult,
  ListFoldersInput,
  ListFoldersResult,
  MoveFolderInput,
  RenameFolderInput,
} from '../models/folder.js'
import {
  CreateProjectInput,
  DeleteProjectInput,
  GetProjectInput,
  ListProjectsByOrganisationInput,
  ListProjectsByOrganisationResult,
  Project,
  UpdateProjectInput,
} from '../models/project.js'
import {
  CreatePhotogrammetryTaskInput,
  CreateTaskInput,
  DeleteTaskInput,
  EstimateTaskInput,
  GetTaskInput,
  ListTasksByProjectInput,
  Task,
  TaskCostEstimate,
  TaskDeletionScope,
  UpdateTaskInput,
} from '../models/task.js'

export class ProjectServiceClient extends ServiceClient {
  public static readonly SERVICE_NAME = 'ProjectService'

  constructor(input: ServiceClientContext) {
    super({ ...input, serviceName: ProjectServiceClient.SERVICE_NAME })
  }

  /* --------------------------------- Project -------------------------------- */

  createProject = async (
    createProjectInput: CreateProjectInput,
    metadata?: DroidMapMetadata,
  ): Promise<Project> => {
    const project = await this.callApiRouteWithToken<
      CreateProjectInput,
      Project
    >({
      method: 'POST',
      path: '/projects',
      body: createProjectInput,
      metadata,
    })

    return project
  }

  getProject = async (
    input: GetProjectInput,
    metadata?: DroidMapMetadata,
  ): Promise<Project> => {
    const project = await this.callApiRouteWithToken<string, Project>({
      method: 'GET',
      path: `/projects/${input.projectId}`,
      metadata,
    })

    return project
  }

  updateProject = async (
    // `orgId` is excluded: a project's organisation is immutable via this
    // endpoint (#2130) and the body forwards only name/folderId, so accepting
    // it would be a silently-ignored no-op.
    input: Omit<UpdateProjectInput, 'orgId'>,
    metadata?: DroidMapMetadata,
  ): Promise<Project> => {
    const project = await this.callApiRouteWithToken<
      Pick<UpdateProjectInput, 'name' | 'folderId'>,
      Project
    >({
      method: 'PATCH',
      path: `/projects/${input.projectId}`,
      body: {
        name: input.name,
        // Forwarded only when present so omitting it leaves the folder
        // unchanged, while an explicit `null` moves the project to the root.
        ...(input.folderId !== undefined && { folderId: input.folderId }),
      },
      metadata,
    })

    return project
  }

  deleteProject = async (
    input: DeleteProjectInput,
    metadata?: DroidMapMetadata,
  ): Promise<Project> => {
    const project = await this.callApiRouteWithToken<string, Project>({
      method: 'DELETE',
      path: `/projects/${input.projectId}`,
      metadata,
    })

    return project
  }

  listProjectsByOrganisation = async (
    input: ListProjectsByOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<ListProjectsByOrganisationResult> => {
    const { orgId, folderId, page, pageSize, filter, sortBy, sortDir, status } =
      input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      })
    if (status) {
      queryParameters.status = status
    }

    // undefined = all folders (param omitted); null = root only (empty value);
    // a folder id = that folder's direct projects.
    if (folderId !== undefined) {
      queryParameters['folderId'] = folderId ?? ''
    }

    return await this.callApiRouteWithToken<
      string,
      ListProjectsByOrganisationResult
    >({
      method: 'GET',
      path: `/organisations/${orgId}/projects`,
      queryParameters,
      metadata,
    })
  }

  /**
   * Every project in an organisation, with no per-caller visibility filter.
   * Answers only an internal-pool caller.
   */
  listProjectsByOrganisationInternal = async (
    input: ListProjectsByOrganisationInput,
    metadata?: DroidMapMetadata,
  ): Promise<ListProjectsByOrganisationResult> => {
    const { orgId, folderId, page, pageSize, filter, sortBy, sortDir, status } =
      input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      })
    if (status) {
      queryParameters.status = status
    }
    if (folderId !== undefined) {
      queryParameters['folderId'] = folderId ?? ''
    }

    return await this.callApiRouteWithToken<
      undefined,
      ListProjectsByOrganisationResult
    >({
      method: 'GET',
      path: `/internal/organisations/${orgId}/projects`,
      queryParameters,
      metadata,
    })
  }

  /* --------------------------------- Folder --------------------------------- */

  /** Create a folder (optionally nested under `parentFolderId`; null = root). */
  createFolder = async (
    input: CreateFolderInput,
    metadata?: DroidMapMetadata,
  ): Promise<Folder> => {
    const { orgId, name, parentFolderId } = input

    return await this.callApiRouteWithToken<
      { name: string; parentFolderId?: string | null },
      Folder
    >({
      method: 'POST',
      path: `/organisations/${orgId}/folders`,
      body: { name, parentFolderId: parentFolderId ?? null },
      metadata,
    })
  }

  /**
   * List the immediate child folders of a parent (omit / null = org root),
   * each with its subtree project count. A caller who can't create projects
   * in the org sees only folders holding a project they can view, with counts
   * limited to those projects, and gets a 404 for any other parent. Folder
   * managers see every folder with org-wide counts (ADR-0015 D3 amendment).
   * Each folder's `preview` sample is always scoped to viewable projects.
   */
  listFolders = async (
    input: ListFoldersInput,
    metadata?: DroidMapMetadata,
  ): Promise<ListFoldersResult> => {
    const { orgId, parentFolderId } = input

    const queryParameters: QueryParameterBag = {}
    // undefined = root (param omitted); a folder id = that folder's children.
    if (parentFolderId != null) {
      queryParameters['parentId'] = parentFolderId
    }

    return await this.callApiRouteWithToken<string, ListFoldersResult>({
      method: 'GET',
      path: `/organisations/${orgId}/folders`,
      queryParameters,
      metadata,
    })
  }

  /**
   * Fetch every folder the caller can see (same rule as {@link listFolders})
   * as a flat list, for building the whole tree client-side (e.g. a
   * move-destination picker). Assemble via `parentFolderId`.
   */
  listFolderTree = async (
    input: ListFolderTreeInput,
    metadata?: DroidMapMetadata,
  ): Promise<ListFolderTreeResult> => {
    return await this.callApiRouteWithToken<string, ListFolderTreeResult>({
      method: 'GET',
      path: `/organisations/${input.orgId}/folders`,
      queryParameters: { tree: 'true' },
      metadata,
    })
  }

  /** Rename a folder (single-segment name). */
  renameFolder = async (
    input: RenameFolderInput,
    metadata?: DroidMapMetadata,
  ): Promise<Folder> => {
    const { folderId, name } = input

    return await this.callApiRouteWithToken<{ name: string }, Folder>({
      method: 'PATCH',
      path: `/folders/${folderId}`,
      body: { name },
      metadata,
    })
  }

  /** Move a folder under a new parent (`null` = org root). */
  moveFolder = async (
    input: MoveFolderInput,
    metadata?: DroidMapMetadata,
  ): Promise<Folder> => {
    const { folderId, parentFolderId } = input

    return await this.callApiRouteWithToken<
      { parentFolderId: string | null },
      Folder
    >({
      method: 'PATCH',
      path: `/folders/${folderId}`,
      body: { parentFolderId },
      metadata,
    })
  }

  /**
   * Delete a folder. Fails with 400 ({@link FOLDER_NOT_EMPTY_ERROR}) if the
   * folder still contains projects or sub-folders.
   */
  deleteFolder = async (
    input: DeleteFolderInput,
    metadata?: DroidMapMetadata,
  ): Promise<Folder> => {
    return await this.callApiRouteWithToken<string, Folder>({
      method: 'DELETE',
      path: `/folders/${input.folderId}`,
      metadata,
    })
  }

  /* ---------------------------------- Task ---------------------------------- */

  createTask = async (
    input: CreateTaskInput | CreatePhotogrammetryTaskInput,
    metadata?: DroidMapMetadata,
  ): Promise<Task> => {
    return await this.callApiRouteWithToken<
      CreateTaskInput | CreatePhotogrammetryTaskInput,
      Task
    >({
      method: 'POST',
      path: `/tasks`,
      body: input,
      metadata,
    })
  }

  /**
   * Pre-flight cost estimate for a mapping-job submission (#2015). Read-only
   * — runs the quota gate's estimate path without creating anything — so the
   * task-create confirmation step can show the estimated gigapixels + dollar
   * cost before the user commits, and flag an about-to-be-blocked job up
   * front. Never 402s; over-limit is reported via `wouldBlock`/`reason`.
   */
  estimateTask = async (
    input: EstimateTaskInput,
    metadata?: DroidMapMetadata,
  ): Promise<TaskCostEstimate> => {
    return await this.callApiRouteWithToken<
      EstimateTaskInput,
      TaskCostEstimate
    >({
      method: 'POST',
      path: `/tasks/estimate`,
      body: input,
      metadata,
    })
  }

  getTask = async (
    input: GetTaskInput,
    metadata?: DroidMapMetadata,
  ): Promise<Task> => {
    return await this.callApiRouteWithToken<string, Task>({
      method: 'GET',
      path: `/tasks/${input.taskId}`,
      metadata,
    })
  }

  updateTask = async (
    input: UpdateTaskInput,
    metadata?: DroidMapMetadata,
  ): Promise<Task> => {
    const task = await this.callApiRouteWithToken<Partial<Task>, Task>({
      method: 'PATCH',
      path: `/tasks/${input.taskId}`,
      body: {
        name: input.name,
        inputAssetSetReferences: input.inputAssetSetReferences,
        outputAssetSetReferences: input.outputAssetSetReferences,
      },
      metadata,
    })

    return task
  }

  deleteTask = async (
    input: DeleteTaskInput,
    metadata?: DroidMapMetadata,
  ): Promise<Task> => {
    const { taskId, deletionScope } = input

    return await this.callApiRouteWithToken<
      { deletionScope: TaskDeletionScope },
      Task
    >({
      method: 'DELETE',
      path: `/tasks/${taskId}`,
      body: {
        deletionScope: deletionScope ?? TaskDeletionScope.DELETE_ALL,
      },
      metadata,
    })
  }

  listTasksByProject = async (
    input: ListTasksByProjectInput,
    metadata?: DroidMapMetadata,
  ): Promise<OffsetPaged<Task>> => {
    const { projectId, page, pageSize, filter, sortBy, sortDir, statuses } =
      input

    const queryParameters: QueryParameterBag = {
      ...queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      }),
      ...(statuses?.length && { status: statuses.join(',') }),
    }

    return await this.callApiRouteWithToken<string, OffsetPaged<Task>>({
      method: 'GET',
      path: `/projects/${projectId}/tasks`,
      metadata,
      queryParameters,
    })
  }
}
